import { Avatar, Box, Chip, Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import React, { ReactNode } from 'react'
import { useLayoutContext } from '../../../context/LayoutProvider/LayoutProvider'

/**
 * Generate avatar initials from a name string
 */
export const getAvatarInitials = (name: string = ''): string => {
  if (!name) return '?'
  const nameParts = name.trim().split(' ').filter(Boolean)
  if (nameParts.length === 0) return '?'
  if (nameParts.length === 1) return nameParts[0][0]?.toUpperCase() || '?'
  return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
}

export type PageHeaderChip = {
  label: string
  color?: string
  tooltip?: string
}

export type PageHeaderProps = {
  /** Name to display next to avatar */
  name: string
  /** URL for avatar image (if available) */
  photoUrl?: string
  /** Fallback color for generated avatar (default: theme-based) */
  avatarColor?: string
  /** Icon to display after divider (e.g., CorporateFareIcon, PersonIcon) */
  icon?: ReactNode
  /** Icon color */
  iconColor?: string
  /** Title text next to icon (e.g., "Organization Details", "Contact Details") */
  title?: string
  /** Status text with automatic color */
  status?: string
  /** Type chip (e.g., organization type) */
  type?: string
  /** Array of vertical tags to display */
  verticals?: string[]
  /** Maximum verticals to show before collapsing into +N chip */
  maxVerticals?: number
  /** Additional chips to display */
  chips?: PageHeaderChip[]
  /** Additional content to render at the end */
  endContent?: ReactNode
  /** Loading state */
  isLoading?: boolean
  /** Loading component to render when loading */
  loadingComponent?: ReactNode
  /** Custom className */
  className?: string
}

const PageHeader: React.FC<PageHeaderProps> = ({
  name,
  photoUrl,
  avatarColor,
  icon,
  iconColor,
  title,
  status,
  type,
  verticals = [],
  maxVerticals = 2,
  chips = [],
  endContent,
  isLoading = false,
  loadingComponent,
  className,
}) => {
  const theme = useTheme()
  const { pinned, agentDock, isAgentDockOpen } = useLayoutContext()
  const isDockSide = isAgentDockOpen && (agentDock === 'left' || agentDock === 'right')
  const dividerMx = isDockSide ? 1 : 2

  // Calculate visible vs overflow verticals
  const visibleVerticals = verticals.slice(0, maxVerticals)
  const overflowVerticals = verticals.slice(maxVerticals)
  const overflowCount = overflowVerticals.length

  // Default avatar color based on theme
  const defaultAvatarColor = avatarColor || theme.palette.primary.main

  if (isLoading && loadingComponent) {
    return <>{loadingComponent}</>
  }

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: isDockSide ? 1 : 3,
        position: 'sticky',
        zIndex: 901,
        marginLeft: 5,
        mr: 1,
        p: 0.8,
        pt: pinned ? '60px' : 0.8,
        transition: 'padding 0.3s ease, gap 0.3s ease',
        overflow: 'hidden',
      }}
    >
      {/* Avatar and Name Section — hidden when agent dock is on a side */}
      {!isDockSide && (
        <Box sx={{ display: 'flex', flexDirection: 'row', ml: 2, flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'left',
              alignItems: 'center',
              height: '100%',
              gap: 4,
            }}
          >
            {photoUrl ? (
              <Avatar src={photoUrl} sx={{ width: 50, height: 50 }} alt={name} />
            ) : (
              <Avatar
                sx={{
                  backgroundColor: defaultAvatarColor,
                  width: 50,
                  height: 50,
                  fontSize: 18,
                  fontWeight: 'bold',
                }}
                alt={name}
              >
                {getAvatarInitials(name)}
              </Avatar>
            )}
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: 20,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Icon and Title Section */}
      {(icon || title) && (
        <>
          {!isDockSide && <Divider orientation="vertical" flexItem sx={{ mx: dividerMx }} />}
          {icon && (
            <Box sx={{ color: iconColor || theme.palette.primary.main, display: 'flex', alignItems: 'center' }}>
              {icon}
            </Box>
          )}
          {title && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: theme.palette.text.secondary,
              }}
            >
              {title}
            </Typography>
          )}
        </>
      )}

      {/* Verticals Section */}
      {verticals.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: dividerMx }} />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            {visibleVerticals.map((vertical, index) => (
              <Chip
                key={`vertical-${index}`}
                label={vertical}
                size="small"
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: renderColorByString(vertical) || theme.palette.text.primary,
                  fontWeight: 500,
                }}
              />
            ))}
            {overflowCount > 0 && (
              <Tooltip
                title={
                  <Stack sx={{ gap: 0.5 }}>
                    {overflowVerticals.map((vertical, index) => (
                      <Typography key={`overflow-${index}`} variant="body2">
                        {vertical}
                      </Typography>
                    ))}
                  </Stack>
                }
                arrow
                placement="bottom"
              >
                <Chip
                  label={`+${overflowCount}`}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    bgcolor: theme.palette.background.default,
                    fontWeight: 600,
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </>
      )}

      {/* Type Chip */}
      {type && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: dividerMx }} />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Chip
              label={type}
              size="small"
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: renderColorByString(type) || theme.palette.text.primary,
                fontWeight: 500,
              }}
            />
          </Box>
        </>
      )}

      {/* Status Section */}
      {status && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: dividerMx }} />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Typography sx={{ color: renderColorByString(status), fontWeight: 500 }}>{status}</Typography>
          </Box>
        </>
      )}

      {/* Additional Chips */}
      {chips.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: dividerMx }} />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            {chips.map((chip, index) => (
              <Tooltip key={`chip-${index}`} title={chip.tooltip || ''} arrow placement="bottom">
                <Chip
                  label={chip.label}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    color: chip.color || renderColorByString(chip.label) || theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </>
      )}

      {/* End Content (for custom elements like compare buttons, approval indicators, etc.) */}
      {endContent && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: dividerMx }} />
          {endContent}
        </>
      )}
    </Box>
  )
}

export default PageHeader
